import { useState, useCallback, useRef } from 'react'

import { FilterMap } from './use-filters-list'
import client from 'api'

export type LookupFetchConfig = {
    url: string,
    fields: string,
    allowedFilters: Array<string>,
    dataFilter?: Function,
    simpleRequest?: Boolean,
}

export type LoadFn = (contextFilters: FilterMap) => Promise<any>
export type LoadItemFn = (contextFilters: FilterMap, localFilters: FilterMap) => Promise<any>
export type ResetFn = (currentIds?: number[]) => void

export const useLookupFetch = (config: LookupFetchConfig) => {
    const [loading, setLoading] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [data, setData] = useState<Array<any>>([])
    const [lastParams, setLastParams] = useState<any>([])
    const _loading = useRef(false)  // loading status for internal use

    const load: LoadFn = useCallback((contextFilters) => {
        return new Promise((resolve, reject) => {
            if (_loading.current) { return }

            const params: any = { fields: config.fields }

            if (!config.url.match(/[?&]action=/) && !config.simpleRequest) {
                params.action = 'gte'
            }

            // add global filters to params
            Object.keys(contextFilters).forEach(fieldName => {
                let fieldNameLower = fieldName.toLowerCase();
                if (config.allowedFilters.some(filter => filter.toLowerCase() === fieldNameLower) && contextFilters[fieldName] !== null) {
                    if (params.action === 'eql' || config.simpleRequest || fieldNameLower.endsWith('_filter')) {
                        params[fieldName] = contextFilters[fieldName]
                    } else {
                        params[`min_${fieldName}`] = contextFilters[fieldName]
                        params[`max_${fieldName}`] = contextFilters[fieldName]
                    }
                }
            })

            // if lookup has isWaiting state, reset it here when we're no
            // longer waiting and we are actually loading stuff
            setLoading(true)
            _loading.current = true

            client.get(config.url, { params })
            .then(res => {
                // if we're not looking for exact match just set the response as data
                let fData = res.data
                if (config.dataFilter) { fData = config.dataFilter(res.data) }

                setLastParams(res.config.params)
                setData(fData)
                setDataLoaded(true)
                setLoading(false)
                _loading.current = false

                resolve(fData)
            })
            .catch(err => {
                setLoading(false);
                _loading.current = false
                reject(err)
            })
        })
    }, [config])

    const loadItem = useCallback((contextFilters: FilterMap, localFilters: FilterMap) => {
        return new Promise((resolve, reject) => {
            const params: any = { fields: config.fields }

            if (!config.url.match(/[?&]action=/) && !config.simpleRequest) {
                params.action = 'gte'
            }

            // add global filters to params
            Object.keys(contextFilters).forEach(fieldName => {
                let fieldNameLower = fieldName.toLowerCase();
                if (config.allowedFilters.some(filter => filter.toLowerCase() === fieldNameLower) && contextFilters[fieldName] !== null) {
                    if (params.action === 'eql' || config.simpleRequest) {
                        params[fieldName.toLowerCase()] = contextFilters[fieldName]
                    } else {
                        params[`min_${fieldName.toLowerCase()}`] = contextFilters[fieldName]
                        params[`max_${fieldName.toLowerCase()}`] = contextFilters[fieldName]
                    }
                }
            })

            // add local filters to params
            Object.keys(localFilters).forEach(fieldName => {
                if (params.action === 'eql' || config.simpleRequest) {
                    params[fieldName.toLowerCase()] = localFilters[fieldName]
                } else {
                    params[`min_${fieldName.toLowerCase()}`] = localFilters[fieldName]
                    params[`max_${fieldName.toLowerCase()}`] = localFilters[fieldName]
                }
            })

            // if lookup has isWaiting state, reset it here when we're no
            // longer waiting and we are actually loading stuff
            setLoading(true)

            client.get(config.url, { params })
            .then(res => {
                setLoading(false)
                let fData = res.data
                if (config.dataFilter) { fData = config.dataFilter(res.data) }

                if (fData.length === 1) {
                    // when data is found resolve with data and let whoever
                    // called this deal with the result without resetting all
                    // the data
                    setData((currentData) => {
                        if (!currentData.find(item => item.HDR_ID === fData[0].HDR_ID)) {
                            return [...currentData, fData[0]]
                        } else {
                            return currentData
                        }
                    })
                    resolve(fData[0])
                } else {
                    // if we don't have a match reject the promise, then the
                    // caller can open the lookup or whatever
                    reject('Not found')
                }
            })
            .catch(err => { setLoading(false); reject(err) })
        })
    }, [config])

    const reset: ResetFn = useCallback((currentIds) => {
        setDataLoaded(false)
        if (currentIds && currentIds.length) {
            setData(data.filter((el: any) => el && currentIds.indexOf(el.HDR_ID) !== -1))
        } else {
            setData([])
        }
    }, [data])

    const _setData = useCallback((data: Array<any>) => {
        setData(data)
    }, [])

    return { data, load, loadItem, reset, loading, setData: _setData, lastParams, dataLoaded }
}
